import React, { useState } from "react";
import { Avatar, ConfirmModal } from "../common";
import { fmtTime, colorForId } from "../../lib/api";
import { Plus, Search, Lock, Users, Radio, Megaphone, CheckSquare, Square, Archive, ArchiveRestore, Trash2, X, Inbox } from "lucide-react";

const FILTERS = [
  { key: "all", label: "TOUTES", testid: "filter-all-tab" },
  { key: "unread", label: "NON LUES", testid: "filter-unread-tab" },
  { key: "private", label: "PRIVÉES", testid: "filter-private-tab" },
  { key: "group", label: "GROUPES", testid: "filter-groups-tab" },
  { key: "feeds", label: "FLUX", testid: "filter-feeds-tab" },
  { key: "scheduled", label: "PROGRAMMÉES", testid: "filter-scheduled-tab" },
];

function typeIcon(t) {
  if (t === "private") return <Lock size={13} />;
  if (t === "group") return <Users size={13} />;
  if (t === "info_descending") return <Megaphone size={13} />;
  return <Radio size={13} />;
}

export default function ConversationList({ channels, selectedId, onSelect, filter, setFilter, search, setSearch, onNew, characters = [], charFilter, setCharFilter, archived = false, setArchived, onBulk }) {
  const [selectMode, setSelectMode] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [confirmDel, setConfirmDel] = useState(false);

  const visible = channels.filter((c) => {
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (charFilter && !(c.participants_view || []).some((p) => p.type === "player_character" && p.id === charFilter)) return false;
    if (filter === "all" || filter === "scheduled") return true;
    if (filter === "unread") return c.unread > 0;
    if (filter === "private") return c.type === "private";
    if (filter === "group") return c.type === "group";
    if (filter === "feeds") return c.type === "info_descending" || c.type === "info_interactive";
    return true;
  });

  function toggleSel(id) {
    setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function exitSelect() { setSelectMode(false); setSel(new Set()); }
  async function runBulk(action) {
    await onBulk?.(action, [...sel]);
    exitSelect();
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-[color:var(--r-primary)]/12">
        <div className="relative mb-2">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--r-text-muted)] pointer-events-none" />
          <input
            data-testid="conversation-search"
            className="input font-mono text-xs"
            style={{ paddingLeft: 36 }}
            placeholder="RECHERCHE"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button data-testid="new-channel-button" className="btn btn-solid w-full flex items-center justify-center gap-2 text-[11px]" onClick={onNew}>
          <Plus size={13} /> NOUVELLE TRANSMISSION
        </button>
        <div className="mt-2 flex items-center gap-2">
          <button
            data-testid="gm-archived-toggle"
            onClick={() => { setArchived?.(!archived); exitSelect(); }}
            className="btn btn-ghost text-[10px] flex items-center gap-1 flex-1 justify-center"
            style={{ borderColor: archived ? "var(--r-primary)" : undefined, color: archived ? "var(--r-primary)" : undefined }}
          >
            {archived ? <Inbox size={11} /> : <Archive size={11} />} {archived ? "ACTIVES" : "ARCHIVÉES"}
          </button>
          <button
            data-testid="gm-select-toggle"
            onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
            className="btn btn-ghost text-[10px] flex items-center gap-1 flex-1 justify-center"
            style={{ borderColor: selectMode ? "var(--r-primary)" : undefined, color: selectMode ? "var(--r-primary)" : undefined }}
          >
            <CheckSquare size={11} /> {selectMode ? "ANNULER" : "SÉLECTIONNER"}
          </button>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="sys-label whitespace-nowrap">TRIER PAR PJ</span>
          <select
            data-testid="pj-filter-select"
            className="input font-mono text-xs"
            style={{ padding: "6px 8px" }}
            value={charFilter || ""}
            onChange={(e) => setCharFilter(e.target.value)}
          >
            <option value="">TOUS LES PJ</option>
            {characters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {characters.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1.5" data-testid="pj-color-legend">
            {characters.map((c) => {
              const col = colorForId(c.id);
              const activePj = charFilter === c.id;
              return (
                <button
                  key={c.id}
                  data-testid={`pj-legend-${c.id}`}
                  onClick={() => setCharFilter(activePj ? "" : c.id)}
                  className="flex items-center gap-1.5 transition-opacity"
                  title={`Filtrer par ${c.name}`}
                  style={{ opacity: !charFilter || activePj ? 1 : 0.4 }}
                >
                  <span style={{ width: 9, height: 9, borderRadius: "9999px", background: col, boxShadow: `0 0 5px ${col}`, flexShrink: 0 }} />
                  <span className="font-mono text-[9px] tracking-wide break-all" style={{ color: activePj ? col : "var(--r-text-muted)" }}>{c.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {!archived && (
        <div className="flex gap-1 px-2 py-2 overflow-x-auto no-scrollbar border-b border-[color:var(--r-primary)]/12">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={f.testid}
              onClick={() => setFilter(f.key)}
              className="font-mono text-[9px] tracking-[0.15em] px-2 py-1 whitespace-nowrap transition-colors"
              style={{
                color: filter === f.key ? "#04131a" : "var(--r-text-muted)",
                background: filter === f.key ? "var(--r-primary)" : "transparent",
                border: `1px solid ${filter === f.key ? "var(--r-primary)" : "transparent"}`,
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}
      {archived && <div className="sys-label px-3 py-2 border-b border-[color:var(--r-primary)]/12 flex items-center gap-2"><Archive size={11} /> TRANSMISSIONS ARCHIVÉES</div>}

      <div className="flex-1 overflow-y-auto">
        {visible.map((c) => {
          const active = c.id === selectedId;
          const checked = sel.has(c.id);
          const npcP = (c.participants_view || []).find((p) => p.type === "npc");
          const pjs = (c.participants_view || []).filter((p) => p.type === "player_character");
          const oneToOne = c.type === "private" && npcP && pjs.length === 1;
          const pj = pjs[0];
          const pjColor = pj ? colorForId(pj.id) : "var(--r-text)";
          return (
            <button
              key={c.id}
              data-testid={`conversation-item-${c.id}`}
              onClick={() => (selectMode ? toggleSel(c.id) : onSelect(c))}
              className="w-full text-left px-3 py-3 flex items-center gap-3 border-b border-[color:var(--r-primary)]/6 transition-colors"
              style={{ background: active && !selectMode ? "color-mix(in srgb, var(--r-primary) 12%, transparent)" : (checked ? "color-mix(in srgb, var(--r-primary) 8%, transparent)" : "transparent"), borderLeft: active && !selectMode ? "2px solid var(--r-primary)" : "2px solid transparent" }}
            >
              {selectMode && (
                <span data-testid={`conv-check-${c.id}`} style={{ color: checked ? "var(--r-primary)" : "var(--r-text-muted)" }}>
                  {checked ? <CheckSquare size={18} /> : <Square size={18} />}
                </span>
              )}
              {oneToOne ? (
                <div className="relative shrink-0" style={{ width: 46, height: 38 }}>
                  <Avatar src={npcP.avatar} name={npcP.name} size={30} />
                  <div className="absolute" style={{ right: 0, bottom: 0, borderRadius: "9999px", padding: 2, background: "var(--r-panel)" }}>
                    <div style={{ borderRadius: "9999px", border: `2px solid ${pjColor}` }}>
                      <Avatar src={pj.avatar} name={pj.name} size={24} />
                    </div>
                  </div>
                </div>
              ) : (
                <Avatar src={(npcP || {}).avatar} name={c.name} size={38} square={c.type !== "private"} />
              )}
              <div className="flex-1 min-w-0">
                {oneToOne ? (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-mono text-[8px] tracking-[0.15em] px-1 py-0.5 shrink-0" style={{ border: "1px solid color-mix(in srgb, var(--r-primary) 40%, transparent)", color: "var(--r-primary)" }}>1:1</span>
                    <span className="font-semibold text-sm break-words leading-tight" style={{ color: "var(--r-primary)" }}>{npcP.name}</span>
                    <span className="text-xs" style={{ color: "var(--r-text-muted)" }}>↔</span>
                    <span className="font-semibold text-sm break-words leading-tight" style={{ color: pjColor }}>{pj.name}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[color:var(--r-text-muted)]">{typeIcon(c.type)}</span>
                    <span className="font-semibold text-sm break-words leading-tight" style={{ color: "var(--r-text)" }}>{c.name}</span>
                  </div>
                )}
                <div className="sys-label truncate mt-0.5" style={{ textTransform: "none", letterSpacing: "normal" }}>
                  {c.last_message ? `${c.last_message.sender_name}: ${c.last_message.content}` : "—"}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="ts text-[color:var(--r-text-muted)]">{c.last_message ? fmtTime(c.last_message.created_at) : ""}</span>
                {c.unread > 0 && (
                  <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "var(--r-alert)", color: "#fff" }}>{c.unread}</span>
                )}
              </div>
            </button>
          );
        })}
        {visible.length === 0 && <div className="sys-label p-4">{archived ? "AUCUNE CONVERSATION ARCHIVÉE" : "AUCUNE TRANSMISSION"}</div>}
      </div>

      {selectMode && (
        <div className="border-t border-[color:var(--r-primary)]/15 panel p-2.5 flex items-center gap-2" data-testid="gm-bulk-bar">
          <span className="sys-label whitespace-nowrap">{sel.size} SÉL.</span>
          {archived ? (
            <button data-testid="gm-bulk-unarchive" disabled={!sel.size} onClick={() => runBulk("unarchive")} className="btn btn-ghost text-[10px] flex items-center gap-1 flex-1 justify-center disabled:opacity-40"><ArchiveRestore size={12} /> DÉSARCHIVER</button>
          ) : (
            <button data-testid="gm-bulk-archive" disabled={!sel.size} onClick={() => runBulk("archive")} className="btn btn-ghost text-[10px] flex items-center gap-1 flex-1 justify-center disabled:opacity-40"><Archive size={12} /> ARCHIVER</button>
          )}
          <button data-testid="gm-bulk-delete" disabled={!sel.size} onClick={() => setConfirmDel(true)} className="btn btn-danger text-[10px] flex items-center gap-1 flex-1 justify-center disabled:opacity-40"><Trash2 size={12} /> SUPPRIMER</button>
          <button data-testid="gm-bulk-cancel" onClick={exitSelect} className="btn btn-ghost text-[10px] px-2"><X size={12} /></button>
        </div>
      )}

      <ConfirmModal
        open={confirmDel}
        onClose={() => setConfirmDel(false)}
        onConfirm={() => { setConfirmDel(false); runBulk("delete"); }}
        title="SUPPRESSION DÉFINITIVE"
        confirmLabel="SUPPRIMER"
        message={`Supprimer définitivement ${sel.size} conversation(s) et tout leur historique ? Cette action est irréversible.`}
      />
    </div>
  );
}
